// One thing to try, said beside the thing it is about (§16.4).
//
// **It was a chip in the dock's strip and that was the wrong place for it.**
// The floating chrome answers about *state* — the readouts are the economy, the
// turn row the clock, the hotbar what you could make, the rail what a drag
// does, the hand what you hold, and a marker on a square says only that
// something is happening there. A hint is not state. It is an instruction with
// a subject, and a sentence naming a pawn while sitting in a panel three
// hundred pixels from that pawn leaves the player to do the hardest part: work
// out which pawn. So it is drawn at its subject, clamped into the free area the
// inset registry reports.
//
// **The shape is Tauric Studio's tour** (`studio/tour/TourOverlay.tsx`) — ring
// the target, float a card beside it — **without the dimming**, and that is the
// whole difference rather than a saving. A tour is modal: it owns the screen,
// and dimming everything else is how it says so. A hint is not: the board stays
// legible and stays *playable*, since the fastest way to finish with a hint is
// to do the thing it suggests. Nothing here takes a click off the board except
// the card itself.

import {
  type CSSProperties,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";

import type { BoardCanvasHandle } from "./BoardCanvas";
import {
  keyframes,
  motion,
  Panel,
  REDUCED_MOTION,
  STILL,
  styled,
  Text,
  useFreeArea,
  useOverlayClip
} from "./kit";
import { type Beak, placeHintCard } from "./hintPlacement";
import { useHintAnchor } from "./useHintAnchor";
import type { Hint } from "./useHints";

/**
 * Wide enough for a sentence and no wider.
 *
 * The card sits over the board, so every pixel of it is a pixel of the game
 * somebody cannot see — which is the argument for one line and no more.
 */
const CARD_WIDTH = 268;
/** What to place against before the real height is known. One line's worth. */
const CARD_MIN = 52;

/**
 * How big the × is.
 *
 * Comfortably past the ~44px a finger wants and far past the fourteen pixels
 * it used to be. The card is a close target too, so this is the *labelled*
 * one rather than the only one — but a control whose whole job is "make this
 * go away" should not be the smallest thing on screen.
 */
const DISMISS_SIZE = 34;

/** The beak's side, in px, and how far it overlaps the card's own border. */
const BEAK_SIZE = 9;

/**
 * Scoped through the kit's `keyframes` rather than written as an `@keyframes`
 * key in `sx` — emotion scopes the name, and a bare block silently does not
 * animate.
 */
const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 0.95; }
  50%  { transform: scale(1.12); opacity: 0.55; }
  100% { transform: scale(1);    opacity: 0.95; }
`;

const arrive = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/** The whole viewport, inert. Only the card inside it takes a pointer. */
const Layer = styled("div")(({ theme }) => ({
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  zIndex: theme.kit.z.boardOverlay
}));

/** The clipped half of the layer, for a ring on a *square*. See below. */
const Clipped = styled("div")({
  position: "fixed",
  inset: 0,
  pointerEvents: "none"
});

/**
 * The ring round the hint's subject.
 *
 * Brand, because it is the accent this interface points with, and it breathes
 * rather than sits — the subject is the one thing on a busy board the player
 * is being asked to find.
 */
const Ring = styled("div")(({ theme }) => ({
  position: "fixed",
  pointerEvents: "none",
  borderRadius: "50%",
  border: `2px solid ${theme.kit.color.brand}`,
  boxShadow: [
    `0 0 0 4px color-mix(in srgb, ${theme.kit.color.brand} 16%, transparent)`,
    `0 0 18px color-mix(in srgb, ${theme.kit.color.brand} 35%, transparent)`
  ].join(", "),
  animation: `${pulse} 1.9s ease-in-out infinite`,
  [REDUCED_MOTION]: STILL
}));

/**
 * The card. A `Panel`, like everything else that floats over the board.
 *
 * **The whole card is the close button**, so it is the one thing on this layer
 * that takes a pointer — and it says so by lifting its edge on the way past.
 */
const Card = styled(Panel)(({ theme }) => ({
  position: "fixed",
  pointerEvents: "auto",
  cursor: "pointer",
  padding: theme.kit.space.sm,
  animation: `${arrive} ${theme.kit.motion.duration.fast}ms ease-out both`,
  ...motion([], ["border-color", "background"]),
  "&:hover": { borderColor: theme.kit.color.lineHi },
  [REDUCED_MOTION]: STILL
}));

/**
 * The card's pointer, as a rotated square carrying two of the card's borders.
 *
 * A triangle drawn with `border-color: transparent` cannot have an outline, so
 * it reads as a shape floating off the card rather than as part of it. The two
 * borders it keeps are chosen per side below.
 */
const BeakShape = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "side"
})<{ side: Exclude<Beak, "none"> }>(({ theme, side }) => {
  // The two sides facing away from the card, so the beak's outline continues
  // the card's own and the join is invisible.
  const edges = {
    left: ["borderLeft", "borderBottom"],
    right: ["borderRight", "borderTop"],
    up: ["borderLeft", "borderTop"],
    down: ["borderRight", "borderBottom"]
  }[side];
  const line = `2px solid ${theme.kit.color.line}`;
  return {
    position: "absolute",
    width: BEAK_SIZE,
    height: BEAK_SIZE,
    background: `color-mix(in srgb, ${theme.kit.color.panel} 93%, transparent)`,
    transform: "rotate(45deg)",
    pointerEvents: "none",
    [edges[0]]: line,
    [edges[1]]: line
  };
});

/**
 * The ×. A real target, and the *labelled* one.
 *
 * It was a 13px glyph with half a unit of padding either side — about fourteen
 * pixels square, on a card that floats over a board somebody is mid-turn on.
 * It carries no hover card: the glyph is the whole sentence, and fewer words is
 * the standing instruction.
 */
const Dismiss = styled("button")(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: DISMISS_SIZE,
  height: DISMISS_SIZE,
  borderRadius: theme.kit.radius.sm,
  border: `1px solid ${theme.kit.color.line}`,
  background: `color-mix(in srgb, ${theme.kit.color.panelRaised} 85%, transparent)`,
  color: theme.kit.color.inkDim,
  font: "inherit",
  fontSize: 20,
  lineHeight: 1,
  cursor: "pointer",
  ...motion([], ["background", "border-color", "color"]),
  "&:hover": {
    background: theme.kit.color.panelRaised,
    borderColor: theme.kit.color.lineHi,
    color: theme.kit.color.ink
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  }
}));

interface Props {
  hint: Hint | null;
  board: RefObject<BoardCanvasHandle | null>;
}

export function HintOverlay({ hint, board }: Props) {
  const [height, setHeight] = useState(CARD_MIN);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Where the card may be **placed**, and what it may paint over, both from the
  // inset registry — the one geometry source (PLAN-UI §28.1). It tracks the
  // window size itself, so there is no resize listener here.
  const bounds = useFreeArea();
  const clip = useOverlayClip();

  // A fresh concept is a fresh card, and its line may wrap to a different
  // number of rows — so the height goes back to the placement default and is
  // re-measured rather than inherited from the last one.
  const concept = hint?.concept.id ?? null;
  useEffect(() => {
    setHeight(CARD_MIN);
  }, [concept]);

  /**
   * A click anywhere else puts the card away.
   *
   * **On the document, never as a backdrop.** A full-screen hit area is how
   * the tour does it and it is exactly what must not happen here: it would
   * swallow the click, so dismissing the card and playing the square under it
   * would be two gestures instead of one. Listening instead means the click
   * lands on the board *and* clears the hint, which is what somebody clicking
   * past a card meant by it.
   *
   * `pointerdown`, not `click`: the board's own interaction is driven by
   * pointer events, so waiting for a click would leave the card up for the
   * whole of a drag across it.
   */
  const hide = hint?.hide;
  useEffect(() => {
    if (!hide) return;
    const away = (event: PointerEvent) => {
      const card = cardRef.current;
      if (card && event.target instanceof Node && card.contains(event.target)) return;
      hide();
    };
    // Capture, so a handler that stops propagation cannot keep the card up.
    window.addEventListener("pointerdown", away, true);
    return () => window.removeEventListener("pointerdown", away, true);
  }, [hide]);

  const anchor = useHintAnchor(hint?.target ?? null, board, bounds);

  // **Measured, not guessed.** The card is one line collapsed and a paragraph
  // open, and placement has to know which — a constant would put the open card
  // through the bottom of the board on the hints with the longest detail.
  // There is no loop in it: the height depends on the content, never on where
  // the card was put.
  useLayoutEffect(() => {
    const element = cardRef.current;
    if (!element) return;
    const read = () => setHeight(Math.round(element.getBoundingClientRect().height));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, [hint]);

  const place = useMemo(
    () => placeHintCard(anchor, { width: CARD_WIDTH, height }, bounds),
    [anchor, height, bounds]
  );

  if (!hint) return null;

  const ring = anchor && (
    <Ring
      data-testid="hint-ring"
      // Position moves per frame, so it is `style`. Anything animated through
      // `sx` mints a fresh emotion class per distinct value, which is how the
      // document once grew to 27,720 style tags in a hundred plies.
      style={{
        left: anchor.x,
        top: anchor.y,
        width: anchor.width,
        height: anchor.height
      }}
    />
  );

  return (
    <Layer>
      {/* A ring on a *square* is a board mark and clips like one, or a hint
          about a piece the chrome happens to be covering draws neatly over the
          text — the failure the clip already exists for. A ring on a control is
          on the chrome by design and must not be clipped. */}
      {hint.target?.kind === "control" ? (
        ring
      ) : (
        <Clipped
          // In `style`, not `sx`: the clip moves with the chrome and emotion
          // mints a permanent class per distinct `sx` value.
          style={{ clipPath: clip }}
        >
          {ring}
        </Clipped>
      )}

      <Card
        ref={cardRef}
        data-testid="hint-card"
        // **The whole card is the close button.** The × was a thirteen-pixel
        // glyph in the corner of a card that floats over a board somebody is
        // trying to play, which makes putting a hint away a small-target task
        // performed under mild irritation. So the card takes the click, the ×
        // stays as the *labelled* control, and both mean the same thing.
        //
        // The × keeps its own handler rather than being decoration, because a
        // div with a click is not reachable by anything but a mouse: the
        // button is what a keyboard and a screen reader get, and the card is
        // the convenience laid over it. It stops propagation so one press is
        // one dismissal.
        onClick={hint.dismiss}
        style={{ left: place.left, top: place.top, width: CARD_WIDTH }}
      >
        {place.beak !== "none" && <BeakMark beak={place.beak} at={place.beakAt} />}

        {/* **One line, and it never grows.** The card used to unfold the
            paragraph on hover, and the × moved out from under the pointer as
            it did — so closing a hint meant chasing the button that had just
            jumped. Reported from play. The detail is gone rather than moved to
            a hover card: a card explaining a card is a card nobody can reach,
            and the whole surface already means *close*.

            **And the line is not a link either.** It used to open the rules at
            the concept's own heading, which cannot survive the card itself
            meaning close: one surface with two meanings is a card that does
            the wrong one about half the time, and the half it gets wrong takes
            the player out of the game mid-turn. The rules are in the pause
            menu. */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Text
            data-testid="hint-line"
            size={12}
            weight={700}
            sx={{ flex: 1, textAlign: "left", lineHeight: 1.35 }}
          >
            <Text tone="brand" size={12} sx={{ mr: 0.75 }}>
              ✦
            </Text>
            {hint.text.line}
          </Text>
          <Dismiss
            type="button"
            data-testid="hint-dismiss"
            aria-label="Stop showing this hint"
            // One press is one dismissal: the card behind it means the same
            // thing, and both firing would write the profile twice.
            onClick={(event) => {
              event.stopPropagation();
              hint.dismiss();
            }}
          >
            ×
          </Dismiss>
        </div>
      </Card>
    </Layer>
  );
}

/** Which two of the card's borders the beak keeps, and where it sits. */
function BeakMark({ beak, at }: { beak: Exclude<Beak, "none">; at: number }) {
  const off = -(BEAK_SIZE / 2) - 1;
  const along = at - BEAK_SIZE / 2;

  const style: CSSProperties =
    beak === "left"
      ? { left: off, top: along }
      : beak === "right"
        ? { right: off, top: along }
        : beak === "up"
          ? { top: off, left: along }
          : { bottom: off, left: along };

  return <BeakShape side={beak} style={style} />;
}
